import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, MapPin, CreditCard } from "lucide-react";

export default function Checkout() {
    const { items, subtotal, shipping, total, clear } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [addresses, setAddresses] = useState([]);
    const [selectedAddrId, setSelectedAddrId] = useState("");
    const [inline, setInline] = useState({
        label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "",
        country: "India", isDefault: true,
    });
    const [creating, setCreating] = useState(false);

    useEffect(() => { document.title = "Checkout | Loomara"; }, []);

    useEffect(() => {
        if (!user) { navigate("/login", { replace: true, state: { from: "/checkout" } }); return; }
        api.get("/addresses").then(({ data }) => {
            setAddresses(data);
            const def = data.find((a) => a.isDefault) || data[0];
            if (def) setSelectedAddrId(def.id);
        });
    }, [user, navigate]);

    if (items.length === 0) {
        return (
            <div className="container-boutique py-24 text-center" data-testid="checkout-empty">
                <h1 className="font-serif text-4xl text-ink-900">Your bag is empty</h1>
                <p className="text-ink-500 mt-3">Add a few favourites first.</p>
                <a href="/shop" className="btn-primary mt-6 inline-flex">Discover the Collection</a>
            </div>
        );
    }

    const saveInlineAddress = async () => {
        const { data } = await api.post("/addresses", inline);
        setAddresses((cur) => [...cur, data]);
        setSelectedAddrId(data.id);
        return data;
    };

    const placeOrder = async () => {
        setCreating(true);
        try {
            let addrId = selectedAddrId;
            if (!addrId) {
                const fresh = await saveInlineAddress();
                addrId = fresh.id;
            }
            const cartPayload = {
                items: items.map((i) => ({ productId: i.productId, quantity: i.qty, size: i.size })),
                addressId: addrId,
            };
            const { data: order } = await api.post("/checkout/create-order", cartPayload);

            if (!order.configured) {
                // Dev/demo flow — confirm without real payment
                if (!window.confirm("Razorpay keys not configured yet. Proceed with demo (no real charge)?")) {
                    setCreating(false);
                    return;
                }
                const { data: verified } = await api.post("/checkout/verify-payment", {
                    orderId: order.orderId,
                    razorpay_order_id: order.razorpayOrderId,
                    razorpay_payment_id: "pay_demo_" + Math.random().toString(36).slice(2, 10),
                    razorpay_signature: "demo",
                });
                clear();
                toast("Order placed ✨ (demo mode)");
                navigate(`/order/${order.orderId}`, { replace: true });
                return;
            }

            // Real Razorpay flow — load script and open checkout
            await loadRazorpayScript();
            const rz = new window.Razorpay({
                key: order.razorpayKeyId,
                amount: order.amount,
                currency: order.currency,
                name: "Loomara",
                description: `Order ${order.orderNumber}`,
                order_id: order.razorpayOrderId,
                prefill: order.prefill,
                theme: { color: "#A8B89A" },
                handler: async (resp) => {
                    try {
                        await api.post("/checkout/verify-payment", {
                            orderId: order.orderId,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        clear();
                        toast("Payment received ♥");
                        navigate(`/order/${order.orderId}`, { replace: true });
                    } catch (e) {
                        toast(e.response?.data?.detail || "Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setCreating(false) },
            });
            rz.open();
        } catch (e) {
            toast(e.response?.data?.detail || "Could not start checkout");
            setCreating(false);
        }
    };

    return (
        <div className="container-boutique py-12" data-testid="checkout-page">
            <p className="eyebrow">Checkout</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink-900 mt-3">Almost there</h1>

            <div className="grid lg:grid-cols-3 gap-10 mt-10">
                <div className="lg:col-span-2 space-y-6">
                    {/* Address */}
                    <div className="card-soft p-6">
                        <h3 className="font-serif text-2xl text-ink-900 flex items-center gap-2"><MapPin size={18} className="text-sage-600" /> Shipping Address</h3>

                        {addresses.length > 0 && (
                            <div className="space-y-3 mt-5">
                                {addresses.map((a) => (
                                    <label key={a.id} className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer ${selectedAddrId === a.id ? "border-sage-500 bg-ivory-100" : "border-sage-300/30"}`} data-testid={`address-radio-${a.id}`}>
                                        <input type="radio" name="addr" value={a.id} checked={selectedAddrId === a.id} onChange={() => setSelectedAddrId(a.id)} className="mt-1" />
                                        <div className="flex-1 text-sm">
                                            <p className="text-ink-900 font-medium">{a.label} · {a.phone}</p>
                                            <p className="text-ink-500 mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</p>
                                        </div>
                                    </label>
                                ))}
                                <button
                                    onClick={() => setSelectedAddrId("")}
                                    className="text-xs uppercase tracking-[0.2em] text-sage-700 hover:text-sage-600"
                                    data-testid="add-new-address-btn"
                                >
                                    + Add a new address
                                </button>
                            </div>
                        )}

                        {(!selectedAddrId) && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                                {[
                                    ["line1", "Address line 1"],
                                    ["line2", "Address line 2 (optional)"],
                                    ["city", "City"],
                                    ["state", "State"],
                                    ["pincode", "Pincode"],
                                    ["phone", "Phone"],
                                ].map(([k, label]) => (
                                    <input
                                        key={k}
                                        placeholder={label}
                                        value={inline[k] || ""}
                                        onChange={(e) => setInline({ ...inline, [k]: e.target.value })}
                                        className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300"
                                        data-testid={`new-addr-${k}`}
                                    />
                                ))}
                                <label className="inline-flex items-center gap-2 text-xs text-ink-500 col-span-2">
                                    <input type="checkbox" checked={!!inline.isDefault} onChange={(e) => setInline({ ...inline, isDefault: e.target.checked })} />
                                    Save as default address
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Items review */}
                    <div className="card-soft p-6">
                        <h3 className="font-serif text-2xl text-ink-900">Order Summary</h3>
                        <div className="divide-y divide-sage-300/20 mt-4">
                            {items.map((i) => (
                                <div key={`${i.productId}-${i.size}`} className="py-3 flex gap-4">
                                    <div className="h-16 w-14 rounded-xl bg-ivory-100 overflow-hidden">
                                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-ink-900">{i.name}</p>
                                        <p className="text-xs text-ink-500 mt-0.5">Size: {i.size || "—"} · Qty: {i.qty}</p>
                                    </div>
                                    <p className="text-sage-700">{inr(i.price * i.qty)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Total + pay */}
                <div className="lg:col-span-1">
                    <div className="card-soft p-6 sticky top-28">
                        <h3 className="font-serif text-2xl text-ink-900 flex items-center gap-2"><CreditCard size={18} className="text-sage-600" /> Payment</h3>
                        <div className="space-y-1.5 text-sm mt-5">
                            <p className="flex justify-between text-ink-700"><span>Subtotal</span><span>{inr(subtotal)}</span></p>
                            <p className="flex justify-between text-ink-700"><span>Shipping</span><span>{shipping === 0 ? <span className="text-sage-600">Free</span> : inr(shipping)}</span></p>
                            <p className="flex justify-between text-ink-900 pt-2 text-lg font-serif"><span>Total</span><span>{inr(total)}</span></p>
                        </div>
                        <button onClick={placeOrder} disabled={creating} className="btn-primary w-full mt-5" data-testid="place-order-btn">
                            {creating ? "Processing…" : `Pay ${inr(total)}`}
                        </button>
                        <p className="text-[11px] text-ink-500 flex items-start gap-2 mt-4">
                            <ShieldCheck size={13} className="text-sage-600 shrink-0 mt-0.5" />
                            Secured by Razorpay · 256-bit encryption · UPI, cards, netbanking & wallets accepted.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function loadRazorpayScript() {
    return new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve();
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = resolve;
        s.onerror = reject;
        document.body.appendChild(s);
    });
}
